/**
 * Decision Code slice - handles decision code state and operations
 * Maps to DecisionCodeController endpoints
 * 
 * NOTE: evaluateDecisionCode and bulkEvaluateDecisionCodes are in returnSlice
 * since they update returnSlip state
 */
import { createAsyncThunk, createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { companyChanged } from 'store/actions/companyActions';
import type { RootState } from 'store';
import axios from 'axios';
import { decisionCodeService } from 'services/decisionCodeService';
import {
    DecisionCode,
    DecisionCodesResult,
} from 'returnCenter-api';
import type { ThunkConf } from 'store/thunks';

interface DecisionCodeState {
    decisionCodes: DecisionCode[] | null;
    ruleSetName: string | null;
    ruleSetId: number | null;
    loading: boolean;
    evaluating: boolean;
    error: string | null;
}

const initialState: DecisionCodeState = {
    decisionCodes: null,
    ruleSetName: null,
    ruleSetId: null,
    loading: false,
    evaluating: false,
    error: null,
};

// ============ THUNKS ============

export const getDecisionCodesForCustomer = createAsyncThunk<DecisionCodesResult, string, ThunkConf>(
    'decisionCode/getForCustomer',
    async (customerCode, { rejectWithValue }) => {
        try {
            return await decisionCodeService.getDecisionCodesForCustomer({ customerCode });
        } catch (error) {
            if (axios.isCancel(error)) return rejectWithValue(undefined);
            return rejectWithValue({ message: error instanceof Error ? error.toString() : 'Unknown error' });
        }
    },
);


// ============ SLICE ============

const decisionCodeSlice = createSlice({
    name: 'decisionCode',
    initialState,
    reducers: {
        setDecisionCodes: (state, action: PayloadAction<DecisionCode[] | null>) => {
            state.decisionCodes = action.payload;
        },
        setRuleSetName: (state, action: PayloadAction<string | null>) => {
            state.ruleSetName = action.payload;
        },
        clearDecisionCodes: (state) => {
            state.decisionCodes = null;
            state.ruleSetName = null;
            state.ruleSetId = null;
        },
        resetDecisionCodeState: () => initialState,
        setEvaluating: (state, action: PayloadAction<boolean>) => {
            state.evaluating = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getDecisionCodesForCustomer.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getDecisionCodesForCustomer.fulfilled, (state, action) => {
                state.loading = false;
                state.decisionCodes = action.payload.decisionCodes ?? null;
                state.ruleSetName = action.payload.ruleSetName ?? null;
                state.ruleSetId = action.payload.ruleSetId ?? null;
            })
            .addCase(getDecisionCodesForCustomer.rejected, (state, action) => {
                state.loading = false;
                state.error = (action.payload as any)?.message ?? 'Failed to load decision codes';
            });

        // Reset on company change
        builder.addCase(companyChanged, () => initialState);
    },
});

// ============ SELECTORS ============

export const selectDecisionCodes = (state: RootState) => state.decisionCode.decisionCodes;
export const selectRuleSetName = (state: RootState) => state.decisionCode.ruleSetName;
export const selectRuleSetId = (state: RootState) => state.decisionCode.ruleSetId;
export const selectDecisionCodeLoading = (state: RootState) => state.decisionCode.loading;
export const selectDecisionCodeEvaluating = (state: RootState) => state.decisionCode.evaluating;
export const selectDecisionCodeError = (state: RootState) => state.decisionCode.error;

export const selectHasDecisionCodes = createSelector(
    selectDecisionCodes,
    (decisionCodes) => decisionCodes !== null && decisionCodes.length > 0,
);

// ============ EXPORTS ============

export const decisionCodeActions = decisionCodeSlice.actions;
export const {
    setDecisionCodes,
    setRuleSetName,
    clearDecisionCodes,
    resetDecisionCodeState,
    setEvaluating,
} = decisionCodeSlice.actions;

export const decisionCodeThunks = {
    getDecisionCodesForCustomer,
    getDecisionCodes: getDecisionCodesForCustomer, // alias for backwards compatibility
};

export const decisionCodeSelectors = {
    selectDecisionCodes,
    selectRuleSetName,
    selectRuleSetId,
    selectDecisionCodeLoading,
    selectDecisionCodeEvaluating,
    selectDecisionCodeError,
    selectHasDecisionCodes,
};

export default decisionCodeSlice.reducer;
